"use client"

import { useState, useEffect } from "react"
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useForm, Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { login, resetAuthState } from "../slices/authSlice"
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Avatar,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { Lock, Chat, Person } from "@mui/icons-material" // Added Person icon

// Styled components
const GreenSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#008069",
  color: "white",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(6),
  height: "100vh",
}))

const FormSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  height: "100vh",
  backgroundColor: "#f5f5f5",
}))

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  borderRadius: theme.spacing(1),
}))

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 254, 0.2)",
  padding: theme.spacing(2),
  marginRight: theme.spacing(2),
  width: 60,
  height: 60,
}))

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: "#008069",
  "&:hover": {
    backgroundColor: "#128C7E",
  },
}))

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { loading, error, userInfo } = useSelector((state) => state.auth)
  const [isRegistered, setIsRegistered] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userName: "", // Changed from email to userName
      password: "",
    },
  })

  useEffect(() => {
    // Check if URL contains the registered parameter
    const urlParams = new URLSearchParams(location.search)
    const registered = urlParams.get("registered") === "true"

    if (registered) {
      setIsRegistered(true)
      toast.success("Registration successful! Please log in.")
    }
  }, [location.search])

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (userInfo) {
      navigate("/dashboard")
    }
  }, [userInfo, navigate])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetAuthState())
    }
  }, [error, dispatch])

  const onSubmit = (data) => {
    dispatch(login(data))
  }

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      {/* Left side - Green background with logo */}
      <Grid item xs={false} sm={false} md={6}>
        <GreenSection>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <LogoAvatar>
              <Chat fontSize="large" />
            </LogoAvatar>
            <Typography variant="h3" component="h1" fontWeight="bold">
              WhatsApp Campaign
            </Typography>
          </Box>
          <Typography variant="h6" align="center" sx={{ maxWidth: 450 }}>
            Manage your WhatsApp marketing campaigns efficiently and reach your audience effectively.
          </Typography>
        </GreenSection>
      </Grid>

      {/* Right side - Login form */}
      <Grid item xs={12} sm={12} md={6}>
        <FormSection>
          <FormPaper elevation={2}>
            <Typography variant="h4" component="h2" align="center" fontWeight="bold" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
              Enter your credentials to access your account
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="userName" // Changed from email to userName
                control={control}
                rules={{
                  required: "Username is required", // Updated validation message
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    id="userName" // Changed id
                    label="Username" // Changed label
                    variant="outlined"
                    error={!!errors.userName} // Updated error reference
                    helperText={errors.userName?.message} // Updated error message reference
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" /> {/* Changed icon from Email to Person */}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(232, 240, 254, 0.5)",
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    fullWidth
                    id="password"
                    label="Password"
                    type="password"
                    variant="outlined"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(232, 240, 254, 0.5)",
                      },
                    }}
                  />
                )}
              />

              {/* <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2" color="textSecondary">
                  Forgot password?
                </Link>
              </Box> */}

              <SubmitButton type="submit" fullWidth variant="contained" disabled={loading}>
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1, color: "white" }} />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </SubmitButton>

              {/* <Box sx={{ mt: 3, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  Don't have an account?{" "}
                  <Link component={RouterLink} to="/register" sx={{ color: "#008069", fontWeight: 500 }}>
                    Sign up
                  </Link>
                </Typography>
              </Box> */}
            </form>
          </FormPaper>
        </FormSection>
      </Grid>
    </Grid>
  )
}

export default LoginPage
